import React from "react";
import axios from "axios";
import { Mail, Sparkles, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "edil-visualizer-lead";

export function readGate() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function VisualizerGate({ onUnlock }) {
  const [email, setEmail] = React.useState("");
  const [consent, setConsent] = React.useState(true);
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      return toast.error("Inserisci un indirizzo email valido");
    }
    if (!consent) return toast.error("Serve il consenso per procedere");
    setLoading(true);
    try {
      await axios.post(`${API}/lead`, { email: v, source: "visualizer", consent });
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ email: v, ts: Date.now() }));
      toast.success("Visualizzatore sbloccato! 🎨");
      onUnlock(v);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Errore: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-tactile p-8 md:p-10 relative overflow-hidden" data-testid="visualizer-gate">
      <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-brand-terracotta/10 blur-2xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 h-44 w-44 rounded-full bg-brand-sage/15 blur-2xl pointer-events-none" />

      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-bg border border-brand-line text-xs font-semibold text-brand-mute">
          <Lock className="h-3.5 w-3.5" /> Accesso esclusivo
        </div>
        <h3 className="heading-display text-2xl md:text-3xl mt-4 leading-tight">
          Inserisci la tua email per provare il <span className="text-brand-terracotta">Visualizzatore AI</span> gratis.
        </h3>
        <p className="text-brand-mute text-sm mt-3 leading-relaxed max-w-xl">
          Una piccola formalità: ci aiuta a tenerti aggiornato su offerte e novità Edil Murgia.
          Niente spam, puoi disiscriverti in qualsiasi momento.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-mute" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tua@email.it"
              className="w-full rounded-full border border-brand-line bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-terracotta/30 focus:border-brand-terracotta"
              data-testid="gate-email-input"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary justify-center"
            data-testid="gate-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Sblocco..." : "Sblocca il Visualizzatore"}
          </button>
        </div>

        <label className="mt-4 flex items-start gap-2.5 text-xs text-brand-mute cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-brand-terracotta cursor-pointer"
            data-testid="gate-consent"
          />
          <span>
            Acconsento al trattamento della mia email per ricevere comunicazioni commerciali
            e aggiornamenti da Edil Murgia (ai sensi del GDPR). Possibilità di revoca in ogni momento.
          </span>
        </label>

        <div className="mt-6 grid grid-cols-3 gap-3 max-w-xl">
          {[
            { k: "100%", v: "Gratis" },
            { k: "60s", v: "Risultato" },
            { k: "5", v: "Stili AI" },
          ].map((s) => (
            <div key={s.v} className="text-center p-3 rounded-xl bg-brand-bg border border-brand-line">
              <div className="heading-display text-xl text-brand-ink">{s.k}</div>
              <div className="text-[10px] uppercase tracking-widest text-brand-mute mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
}
